import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, RefreshCcw, Trash2, ExternalLink, Sheet as SheetIcon, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const TYPE_LABELS = {
  enrollments: "Enrollments",
  members: "Members",
  fbc_orders: "Full Body Checkup",
  medicine_orders: "Medicine orders",
  lab_test_orders: "Lab test orders",
};

const REQUIRED_COLS = {
  enrollments: "applicant_name, phone, email, category, insurance_eligible, pin_code",
  members: "full_name, phone, email, category, insurance_eligible, pin_code, address, gender",
  fbc_orders: "member_phone, slot_date (YYYY-MM-DD), slot_time, amount",
  medicine_orders: "member_phone, items (e.g. 'Paracetamol x2, Cetrizine x1'), amount, notes",
  lab_test_orders: "member_phone, tests (comma-separated), slot_date (YYYY-MM-DD), slot_time, amount",
};

const emptyForm = { label: "", sheet_type: "enrollments", sheet_url: "", sheet_tab: "", active: true };

export default function Sheets() {
  const [sheets, setSheets] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [syncingId, setSyncingId] = useState(null);
  const [syncingAll, setSyncingAll] = useState(false);

  const load = async () => {
    const r = await api.get("/sheets");
    setSheets(r.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.label || !form.sheet_url) { toast.error("Label and Sheet URL are required"); return; }
    try {
      await api.post("/sheets", form);
      toast.success("Sheet connected");
      setOpen(false);
      setForm(emptyForm);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add sheet");
    }
  };

  const syncOne = async (id) => {
    setSyncingId(id);
    try {
      const r = await api.post(`/sheets/${id}/sync`);
      toast.success(`Synced: +${r.data.added} added, ${r.data.skipped} skipped`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally { setSyncingId(null); }
  };

  const syncAll = async () => {
    setSyncingAll(true);
    try {
      const r = await api.post("/sheets/sync-all");
      const total = r.data.results.reduce((a, x) => a + (x.added || 0), 0);
      toast.success(`Synced ${r.data.count} sheet(s) · ${total} new rows`);
      load();
    } catch { toast.error("Sync-all failed"); }
    finally { setSyncingAll(false); }
  };

  const toggle = async (s) => {
    await api.patch(`/sheets/${s.id}`, { ...s, active: !s.active });
    load();
  };

  const remove = async (id) => {
    await api.delete(`/sheets/${id}`);
    load();
  };

  return (
    <div className="p-8 space-y-6">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="label-caps text-accent mb-2">Data sources</div>
          <h1 className="font-heading text-4xl tracking-tight text-primary">Google Sheets sync</h1>
          <p className="text-muted-foreground mt-2 max-w-2xl">
            Connect public Google Sheets as the source of truth for enrollments, members, and orders.
            Rows are pulled every 5 minutes and de-duplicated by phone / booking slot.
          </p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="sync-all-btn" onClick={syncAll} disabled={syncingAll || sheets.length === 0} variant="outline" className="rounded-full">
            <RefreshCcw size={16} className={`mr-1.5 ${syncingAll ? "animate-spin" : ""}`}/> Sync all now
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-sheet-btn" className="bg-primary hover:bg-primary/90 rounded-full"><Plus size={16} className="mr-1.5"/> Connect sheet</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle className="font-heading text-2xl">Connect a Google Sheet</DialogTitle></DialogHeader>
              <div className="space-y-4 mt-2">
                <div>
                  <Label>Label</Label>
                  <Input data-testid="sheet-label-input" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="e.g. Delhi camp enrollments" />
                </div>
                <div>
                  <Label>Data type</Label>
                  <Select value={form.sheet_type} onValueChange={(v) => setForm({ ...form, sheet_type: v })}>
                    <SelectTrigger data-testid="sheet-type-select"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      {Object.entries(TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Sheet URL (must be public — "Anyone with the link · Viewer")</Label>
                  <Input data-testid="sheet-url-input" value={form.sheet_url} onChange={(e) => setForm({ ...form, sheet_url: e.target.value })} placeholder="https://docs.google.com/spreadsheets/d/..." />
                </div>
                <div>
                  <Label>Tab name (optional)</Label>
                  <Input data-testid="sheet-tab-input" value={form.sheet_tab} onChange={(e) => setForm({ ...form, sheet_tab: e.target.value })} placeholder="Leave empty for first tab" />
                </div>
                <div className="text-xs text-muted-foreground bg-secondary rounded-lg p-3 leading-relaxed">
                  <div className="font-medium text-primary mb-1">Expected columns (row 1):</div>
                  {REQUIRED_COLS[form.sheet_type]}
                </div>
              </div>
              <DialogFooter className="mt-4">
                <Button data-testid="save-sheet-btn" onClick={submit} className="bg-primary hover:bg-primary/90">Connect</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      <Card className="border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Label</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Sheet</TableHead>
              <TableHead>Last sync</TableHead>
              <TableHead>Last result</TableHead>
              <TableHead>Active</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sheets.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center py-10 text-muted-foreground">
                <SheetIcon className="mx-auto mb-2 text-muted-foreground/60" size={28}/>
                No sheets connected yet. Click <span className="font-medium text-primary">Connect sheet</span> to add one.
              </TableCell></TableRow>
            )}
            {sheets.map((s) => (
              <TableRow key={s.id} data-testid={`sheet-row-${s.id}`}>
                <TableCell><div className="font-medium">{s.label}</div>{s.sheet_tab && <div className="text-xs text-muted-foreground">Tab: {s.sheet_tab}</div>}</TableCell>
                <TableCell><Badge variant="secondary" className="bg-secondary text-primary">{TYPE_LABELS[s.sheet_type]}</Badge></TableCell>
                <TableCell>
                  <a href={s.sheet_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline text-sm">
                    Open <ExternalLink size={12}/>
                  </a>
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {s.last_sync_at ? new Date(s.last_sync_at).toLocaleString() : "—"}
                </TableCell>
                <TableCell>
                  {s.last_result ? (
                    <div className="text-xs">
                      <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 size={12}/> +{s.last_result.added}</span>
                      <span className="ml-2 text-muted-foreground">skipped {s.last_result.skipped}</span>
                      {s.last_result.errors?.length > 0 && (
                        <div className="mt-1 inline-flex items-center gap-1 text-amber-700"><AlertCircle size={12}/> {s.last_result.errors.length} row error(s)</div>
                      )}
                    </div>
                  ) : <span className="text-xs text-muted-foreground">Not synced yet</span>}
                </TableCell>
                <TableCell><Switch data-testid={`sheet-toggle-${s.id}`} checked={s.active} onCheckedChange={() => toggle(s)}/></TableCell>
                <TableCell className="text-right">
                  <div className="flex gap-2 justify-end">
                    <Button data-testid={`sync-${s.id}`} onClick={() => syncOne(s.id)} size="sm" variant="outline" disabled={syncingId === s.id} className="rounded-full h-8">
                      <RefreshCcw size={14} className={`mr-1 ${syncingId === s.id ? "animate-spin" : ""}`}/> Sync
                    </Button>
                    <Button data-testid={`sheet-delete-${s.id}`} onClick={() => remove(s.id)} size="icon" variant="ghost" className="text-destructive hover:bg-red-50 h-8 w-8">
                      <Trash2 size={14}/>
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Card className="p-6 border-border bg-secondary/40">
        <div className="label-caps text-primary mb-2">Setup tip</div>
        <ol className="text-sm text-muted-foreground space-y-1.5 list-decimal ml-5">
          <li>Open your Google Sheet → <span className="font-medium">Share</span> → General access: <span className="font-medium">Anyone with the link · Viewer</span>.</li>
          <li>Ensure row 1 contains the expected column headers (case-insensitive). Extra columns are ignored.</li>
          <li>Paste the full URL here and pick the matching data type.</li>
          <li>The dashboard pulls new rows every 5 minutes and skips duplicates (by phone for members/enrollments; by phone + slot for orders).</li>
        </ol>
      </Card>
    </div>
  );
}
